import * as React from "react";
// import { useRef } from "react"
import Helmet from "react-helmet";
import { useHotkeys } from "../../tools/react-hotkeys-hook";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import withContext from "../../tools/withContext";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { appPageRootStyles } from "../appBar/appBarTheme";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Labels from "./components/Labels";
import Actions from "./components/Actions";
import LabelisationHead from "./components/LabelisationHead";
import Progression from "./components/Progression";
import SkipDialog from "../common/SkipDialog";
import SkippedItemBanner from "../common/SkippedItemBanner";
import AlertDialog from "../common/AlertDialog";
import LabelerSnackbar from "../common/SnackBar";
import DrawerPhoto from "./components/drawerPhoto";
import LabelerSegmentationStore from "../../store/segmentation-store/labelerSegmentationStore.js";
import LabelerSegmentationUiStore from "../../store/segmentation-store/labelerSegmentationUiStore.js";
import FabricSegmentationStore from "../../store/segmentation-store/fabricSegmentationStore.js";
import deleteLastPointHandler from "../../store/segmentation-store/deleteLastPointHandler";
import copyPolygonsHandler from "../../store/segmentation-store/copyPolygonsHandler";
import pastePolygonsHandler from "../../store/segmentation-store/pastePolygonsHandler";
import saveCopyNextImageHandler from "../../store/segmentation-store/saveCopyNextImageHandler";
import saveAndNextImageHandler from "../../store/segmentation-store/saveAndNextImageHandler";
import saveHandler from "../../store/segmentation-store/saveHandler";

const i18nMapping: any = {
  UNREADABLE: "Image floue",
  CROPPED: "Image tronquée",
  OCCLUSION: "Occlusion",
  null: "-",
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...appPageRootStyles(theme),
      display: "flex",
      flexDirection: "row",
    },
    canvasContainer: {
      backgroundColor: "#edf2ff",
      margin: "0 auto",
      width: "100%",
      height: "100%",
    },
    nbrImagesG: {
      padding: `${theme.spacing.unit}px 0`,
    },
    button: {
      lineHeight: "27px",
    },
    motifButton: {
      textAlign: "center",
      marginTop: theme.spacing.unit * 2,
    },
    centered: {
      textAlign: "center",
    },
    fullWidth: {
      width: "100%",
    },
  });

const LabelerSegmentation = (props: any) => {
  const {
    classes,
    match: {
      params: { noImage },
    },
    ctx: { appFetch, user },
    history,
    labelerSegmentationStore,
    labelerSegmentationUiStore,
    fabricSegmentationStore,
  } = props;

  const {
    batch,
    numImage,
    images,
    tags,
    motifs,
    labeledImages,
    changeMotif,
    deleteAllSegments,
    isReadOnly,
  } = labelerSegmentationStore as LabelerSegmentationStore;

  const {
    polygons,
    selectMode,
    deleteMode,
    openSkipDialog,
    setOpenSkipDialog,
    openAlertDialog,
    alertDialogText,
    alertDialogCallback,
    alertDialogCloseCallback,
    setOpenAlertDialog,
    isSnackbarOpen,
    setIsSnackbarOpen,
    messageSnackBar,
    variantSnackBar,
  } = labelerSegmentationUiStore as LabelerSegmentationUiStore;

  const {
    configure,
    image,
    canvas,
    drawPolygon,
    bindKeyboardEvents,
    removeImageFromCanvas,
  } = fabricSegmentationStore as FabricSegmentationStore;

  const canvasRef = React.createRef<HTMLCanvasElement>();
  const canvasWrapperRef = React.createRef<HTMLDivElement>();

  const currentImage = images[Number(noImage)];
  const userLogin = user?.login || user?.email || null;

  const handleChangeMotif = (motifSkip: string | null, clear: boolean) => {
    changeMotif(motifSkip, clear);
    saveHandler(labelerSegmentationStore.store, appFetch, history, userLogin);
  };

  React.useEffect(() => {
    if (
      canvasRef.current !== null &&
      canvasWrapperRef.current !== null &&
      (canvas === undefined || Number(noImage) !== Number(numImage))
    ) {
      if (canvas !== undefined && Number(noImage) !== Number(numImage)) {
        deleteAllSegments();
        removeImageFromCanvas();
      }
      configure(canvasRef.current, canvasWrapperRef.current, Number(noImage));
    }
  }, [noImage, numImage, canvas, canvasRef, canvasWrapperRef, configure, deleteAllSegments, removeImageFromCanvas]);

  React.useEffect(() => {
    polygons.map((p) => drawPolygon(p));
  }, [polygons, selectMode, deleteMode]);

  // HOT KEYS /////////
  useHotkeys("ctrl+z, command+z", (e) => {
    if (isReadOnly) return;
    deleteLastPointHandler(labelerSegmentationStore.store);
  });

  useHotkeys("ctrl+c, command+c", (e) => {
    if (isReadOnly) return;
    copyPolygonsHandler(labelerSegmentationStore.store);
  });

  useHotkeys("ctrl+v, command+v", (e) => {
    if (isReadOnly) return;
    pastePolygonsHandler(labelerSegmentationStore.store);
  });

  useHotkeys("ctrl+i, command+i", (e) => {
    if (isReadOnly) return;
    saveCopyNextImageHandler(
      labelerSegmentationStore.store,
      appFetch,
      history,
      userLogin
    );
  });

  useHotkeys(
    "ctrl+s, command+s",
    (e) => {
      if (isReadOnly) return;
      const handled = saveAndNextImageHandler(
        labelerSegmentationStore.store,
        appFetch,
        history,
        userLogin
      );
      if (handled) e.preventDefault();
    },
    [appFetch, history, labelerSegmentationStore, isReadOnly, userLogin]
  );

  useHotkeys("*", (e) => {
    if (isReadOnly && !["Shift", "Escape", "ArrowUp", "ArrowDown"].includes(e.key)) {
      return;
    }
    bindKeyboardEvents(e);
  });
  ////////////////////

  return (
    <Grid
      container
      spacing={16}
      direction="row"
      justify="flex-start"
      className={classes.root}
    >
      <Helmet>
        <title>
          Segm. / {batch?.name?.split("-")[0]} / {noImage}
        </title>
      </Helmet>
      <DrawerPhoto images={labeledImages} />
      <SkipDialog
        open={openSkipDialog}
        motifs={motifs}
        motifSelected={currentImage && currentImage.motif}
        onAccept={handleChangeMotif}
        onClose={() => {
          setOpenSkipDialog(false);
        }}
        i18nMapping={i18nMapping}
        defaultMotifIndex={1}
      />
      <AlertDialog
        open={openAlertDialog}
        onClose={() => {
          setOpenAlertDialog(false);
          alertDialogCloseCallback();
        }}
        text={alertDialogText}
        onAccept={() => {
          setOpenAlertDialog(false);
          alertDialogCallback();
        }}
        acceptLabel="Confirmer"
      />
      <LabelerSnackbar
        open={isSnackbarOpen}
        onClose={() => {
          setIsSnackbarOpen(false);
        }}
        variant={variantSnackBar}
        message={messageSnackBar}
      />
      {/* ************** IMAGE ********************/}
      <Grid item sm={7} style={{ flexGrow: 1 }}>
        <Grid
          container
          direction="column"
          component={Paper}
          alignItems="stretch"
          style={{ height: "100%", padding: "16px", borderRadius: "16px" }}
        >
          <Grid item>
            <LabelisationHead />
          </Grid>
          <Grid item style={{ flexGrow: 1, padding: 0, marginTop: 8 }}>
            <div className={classes.canvasContainer} ref={canvasWrapperRef}>
              <canvas ref={canvasRef} />
            </div>
          </Grid>
        </Grid>
      </Grid>

      {/* ************** LABELLING *******************/}
      <Grid item sm={5} style={{ flexGrow: 1, height: "100%" }}>
        <Grid
          container
          component={Paper}
          direction="column"
          justify="space-between"
          alignItems="stretch"
          style={{
            height: "100%",
            padding: "16px",
            borderRadius: "16px",
            overflow: "scroll",
          }}
        >
          {/* LABELS */}
          <Grid item className={classes.fullWidth}>
            <Grid container direction="column" spacing={16}>
              <Grid item className={classes.fullWidth}>
                <Progression
                  total={images.length}
                  nbrLabelisees={labeledImages.length}
                />
              </Grid>
              <Grid item>
                <Labels labels={tags} />
              </Grid>
            </Grid>
          </Grid>

          {currentImage && currentImage.motif ? (
            <Grid
              item
              style={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: 0,
              }}
            >
              <SkippedItemBanner
                motifLabel={
                  i18nMapping[currentImage.motif] || currentImage.motif
                }
              />
            </Grid>
          ) : (
            <Grid item style={{ flexGrow: 1 }} />
          )}

          {/* ACTIONS (FIXÉES EN BAS DU PANEL) */}
          <Grid
            item
            className={classes.fullWidth}
            style={{ height: "fit-content" }}
          >
            <Grid container direction="column">
              {image && (
                <Grid item>
                  <Actions />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(
  withContext(
    withRouter(
      inject(
        "labelerSegmentationStore",
        "labelerSegmentationUiStore",
        "fabricSegmentationStore"
      )(observer(LabelerSegmentation))
    )
  )
);
